// src/components/TextCompare/ComparisonStats.tsx

import React from 'react';
import { CheckCircle, AlertCircle, TrendingUp, TrendingDown, Hash, Activity, ArrowRightLeft } from 'lucide-react';

interface ComparisonStatsData {
    additions: number;
    deletions: number;
    modifications: number;
    moves: number;
}

interface ComparisonStatsProps {
    stats: ComparisonStatsData | null;
    matchPercentage: number;
    diffCount: number;
}

const ComparisonStats: React.FC<ComparisonStatsProps> = ({ stats, matchPercentage, diffCount }) => {
    if (!stats && (matchPercentage === undefined || matchPercentage === null)) return null;

    const additions = stats?.additions || 0;
    const deletions = stats?.deletions || 0;
    const modifications = stats?.modifications || 0;
    const moves = stats?.moves || 0;

    const getStatusColor = (percent: number) => {
        if (percent >= 90) return '#10b981'; // Green
        if (percent >= 70) return '#f59e0b'; // Amber
        return '#ef4444'; // Red
    };

    const statusColor = getStatusColor(matchPercentage);

    return (
        <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
                <div
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-50 border-2"
                    style={{ borderColor: statusColor }}
                >
                    {matchPercentage > 80 ? (
                        <CheckCircle size={18} style={{ color: statusColor }} />
                    ) : (
                        <AlertCircle size={18} style={{ color: statusColor }} />
                    )}
                </div>
                <div className="flex flex-col justify-center">
                    <span className="text-[1.1rem] font-bold text-slate-800 leading-none">{matchPercentage}%</span>
                    <span className="text-[0.65rem] uppercase text-slate-500 font-semibold tracking-wider">Similarity</span>
                </div>
            </div>

            <div className="w-px h-6 bg-slate-200"></div>

            <div className="flex gap-2">
                <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[0.8rem] font-semibold bg-green-500/12 text-green-900" title="Additions">
                    <TrendingUp size={14} />
                    <span className="font-medium opacity-85">{additions}</span>
                    <span className="font-medium opacity-85">Additions</span>
                </div>

                <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[0.8rem] font-semibold bg-red-500/12 text-red-900" title="Deletions">
                    <TrendingDown size={14} />
                    <span className="font-medium opacity-85">{deletions}</span>
                    <span className="font-medium opacity-85">Deletions</span>
                </div>

                {modifications > 0 && (
                    <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[0.8rem] font-semibold bg-indigo-500/12 text-indigo-700" title="Modifications">
                        <Activity size={14} />
                        <span className="font-medium opacity-85">{modifications}</span>
                        <span className="font-medium opacity-85">Changes</span>
                    </div>
                )}

                {moves > 0 && (
                    <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[0.8rem] font-semibold bg-yellow-200/40 text-yellow-700" title="Moved Blocks">
                        <ArrowRightLeft size={14} />
                        <span className="font-medium opacity-85">{moves}</span>
                        <span className="font-medium opacity-85">Moves</span>
                    </div>
                )}

                <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-md text-[0.8rem] font-semibold bg-slate-100 text-[#3c4043]" title="Total Diff Points">
                    <Hash size={14} />
                    <span className="font-medium opacity-85">{diffCount}</span>
                    <span className="font-medium opacity-85">Diff Points</span>
                </div>
            </div>
        </div>
    );
};

export default ComparisonStats;
